/* eslint-disable no-duplicate-case */
import { DateTime } from '@mode-switch/express';
import { DatePart } from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { convertToUTC } from '@mode/shared/util-js';
import { CompiledFormula } from '@mode/shared/util-switch';
import { addMinutes, format, getMonth, getQuarter, getYear } from 'date-fns';

const QuarterNames: { [key: number]: string } = {
  1: 'Q1',
  2: 'Q2',
  3: 'Q3',
  4: 'Q4',
};

const QuarterFullNames: { [key: number]: string } = {
  1: '1st quarter',
  2: '2nd quarter',
  3: '3rd quarter',
  4: '4th quarter',
};

const MonthNames: { [key: number]: string } = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

const WeekdayNames: { [key: number]: string } = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export const DateFormatStrings: Record<ChartTypes.DateFormat, string> = {
  date: 'MMM d y',
  hour: 'y-MMM-d HH:mm:ss',
  full_mon: 'MMM y',
  full_day: 'd MMM y',
  usa_full: 'MMM d y',
  mon_day: 'MM/dd',
  year: 'y',
  numeric_date: 'y-MM-dd',
  usa_default: 'LL/d/y',
  auto: 'LL/d/y',
  none: 'LL/d/y',
};

export const DatePartFormatStrings: Record<DatePart, string> = {
  [DatePart.YEAR]: 'y',
  [DatePart.QUARTER]: 'QQQ y',
  [DatePart.MONTH]: 'MMM y',
  [DatePart.WEEK]: 'MMM d y',
  [DatePart.WEEKDAY]: 'EEEE',
  [DatePart.DAY]: 'MMM d y',
  [DatePart.DAYOFYEAR]: 'd',
  [DatePart.HOUR]: 'haaa MMM d y',
  [DatePart.MINUTE]: 'h:mmaaa MMM d y',
  [DatePart.SECOND]: 'h:mm:ssaaa MMM d y',
};

export const QuarterNamesByNumber: { [name: string]: number } = {};
for (const quarter in QuarterNames) {
  if (QuarterNames.hasOwnProperty(quarter)) {
    QuarterNamesByNumber[QuarterNames[quarter]] = parseInt(quarter);
  }
}

export const MonthNamesByNumber: { [name: string]: number } = {};
for (const month in MonthNames) {
  if (MonthNames.hasOwnProperty(month)) {
    MonthNamesByNumber[MonthNames[month]] = parseInt(month);
  }
}

export const WeekdayNamesByNumber: { [name: string]: number } = {};
for (const weekday in WeekdayNames) {
  if (WeekdayNames.hasOwnProperty(weekday)) {
    WeekdayNamesByNumber[WeekdayNames[weekday]] = parseInt(weekday);
  }
}

export function makeFormattedTimestamp(
  datePart: DatePart,
  dateFormat: ChartTypes.DateFormat,
  timestamp: string | number,
  isXAxisLabels?: boolean,
  yearStart?: DateTime.YearStart
): string {
  // If the timestamp is already a string, then return it.
  // This is the case for DateTruncs in Pie/Donut.
  if (typeof timestamp === 'string') {
    return timestamp;
  }

  // for discrete series containing null values we wish to change the xAxis label to the string 'null'
  if (timestamp == null) {
    return 'null';
  }

  const localDate = new Date(timestamp);
  // Same mask for both DatePartFormatStrings|DateFormatStrings
  if (dateFormat === 'year') {
    return formatDateTruncYear(localDate, yearStart);
  }

  const isDateFormatAutoOrNone = dateFormat === 'auto' || dateFormat === 'none';
  const useDatePartFormats = !isXAxisLabels || isDateFormatAutoOrNone;

  // YEAR|QUARTER requires fiscal handling in DatePart formats
  if (useDatePartFormats) {
    switch (datePart) {
      case DatePart.YEAR:
        return formatDateTruncYear(localDate, yearStart);
      case DatePart.QUARTER:
        return formatDateTruncQuarter(localDate, yearStart);
    }
  }

  const date = dateWithOffset(new Date(timestamp));

  switch (datePart) {
    case DatePart.YEAR:
    case DatePart.QUARTER:
    case DatePart.MONTH:
    case DatePart.WEEK:
    case DatePart.WEEKDAY:
    case DatePart.DAY:
    case DatePart.DAYOFYEAR:
    case DatePart.HOUR:
    case DatePart.MINUTE:
    case DatePart.SECOND:
      return useDatePartFormats
        ? format(date, DatePartFormatStrings[datePart])
        : format(date, DateFormatStrings[dateFormat]);
  }

  return format(date, DateFormatStrings['usa_default']);
}

function dateWithOffset(date: Date): Date {
  return addMinutes(date, date.getTimezoneOffset());
}

export function getDatePart(field: CompiledFormula): DatePart | null {
  if (field.hasDateFunc) {
    switch (field.dateFuncUnit) {
      case DateTime.Unit.Quarter:
        return DatePart.QUARTER;
      case DateTime.Unit.Year:
        return DatePart.YEAR;
      case DateTime.Unit.Month:
        return DatePart.MONTH;
      case DateTime.Unit.Week:
        return DatePart.WEEK;
      case DateTime.Unit.Weekday:
        return DatePart.WEEKDAY;
      case DateTime.Unit.Week:
        return DatePart.WEEK;
      case DateTime.Unit.DayOfYear:
        return DatePart.DAYOFYEAR;
      case DateTime.Unit.Day:
        return DatePart.DAY;
      case DateTime.Unit.Hour:
        return DatePart.HOUR;
      case DateTime.Unit.Minute:
        return DatePart.MINUTE;
      case DateTime.Unit.Second:
        return DatePart.SECOND;
    }
  }

  return null;
}

export function makeDatePartName(field: CompiledFormula, value: any): string {
  if (field.hasDatePart) {
    switch (field.dateFuncUnit) {
      case DateTime.Unit.Year:
        return makeDatePartYearName(value, field.yearStart ?? field.defaultYearStart);
      case DateTime.Unit.Quarter:
        return makeDatePartQuarterName(value);
      case DateTime.Unit.Month:
        return makeDatePartMonthName(value);
      case DateTime.Unit.Weekday:
        return makeDatePartWeekdayName(value);
      case DateTime.Unit.Hour:
        return makeDatePartHourName(value);
      case DateTime.Unit.Minute:
        return makeDatePartMinuteName(value);
      case DateTime.Unit.Second:
        return makeDatePartSecondName(value);
    }
  }

  return value == null ? value : value.toString();
}

export function makeDatePartYearName(value: any, yearStart?: DateTime.YearStart): string {
  if (value == null) {
    return value;
  }

  const startMonth = DateTime.yearStartMonth(yearStart ?? DateTime.YearStart.January);
  if (startMonth > 1) {
    return `FY ${value}`;
  }

  return `${value}`;
}

export function makeDatePartQuarterName(value: any): string {
  return QuarterNames[value] || (value == null ? value : value.toString());
}

export function makeDatePartQuarterFullName(value: any): string {
  return QuarterFullNames[value] || (value == null ? value : value.toString());
}

export function makeDatePartMonthName(value: any): string {
  return MonthNames[value] || (value == null ? value : value.toString());
}

export function makeDatePartWeekdayName(value: any): string {
  return WeekdayNames[value] || (value == null ? value : value.toString());
}

export function makeDatePartHourName(value: any): string {
  return value == null ? value : value.toString();
}

export function makeDatePartMinuteName(value: any): string {
  return value == null ? value : value.toString();
}

export function makeDatePartSecondName(value: any): string {
  return value == null ? value : value.toString();
}

export function formatDateTruncYear(date: Date, yearStart?: DateTime.YearStart): string {
  const parts = getFiscalParts(convertToUTC(date), yearStart);
  const prefix = parts.isFiscal ? 'FY ' : '';
  return `${prefix}${parts.year}`;
}

export function formatDateTruncQuarter(date: Date, yearStart?: DateTime.YearStart): string {
  const parts = getFiscalParts(convertToUTC(date), yearStart);
  const prefix = parts.isFiscal ? 'FY ' : '';
  return `Q${parts.quarter} ${prefix}${parts.year}`;
}

export type FiscalParts = { isFiscal: boolean; year: number; quarter: number };
export function getFiscalParts(date: Date | number, yearStart?: DateTime.YearStart): FiscalParts {
  const startMonth = DateTime.yearStartMonth(yearStart ?? DateTime.YearStart.January);
  const isFiscal = startMonth !== 1;

  if (!isFiscal) {
    return {
      isFiscal,
      year: getYear(date),
      quarter: getQuarter(date),
    };
  }

  const year = getYear(date);
  const month = getMonth(date) + 1;
  const fiscalYear = year + Math.floor((month + 12 - (startMonth % 12)) / 12);
  const fiscalQuarter = Math.floor(((month + 12 - startMonth) % 12) / 3) + 1;

  return {
    isFiscal,
    year: fiscalYear,
    quarter: fiscalQuarter,
  };
}
